import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration";
import { setPostStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import { ButtonWrapper } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import { GridItemStyle } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { css } from "styled-components";

// For production, include a set of commonly used widgets in the main bundle
// the rest will be split into their own chunks
// Do not do this for the ./Overrides folder
const widgets = IS_PRODUCTION
    ? require.context("./Widgets", true, /(Header|Basic|Common|Footer|SignIn)\/.+?\.tsx$/)
    : require.context("./Widgets", true, /\.tsx$/);
// eslint-disable-next-line spaced-comment
//enable hot module reloading for widgets
const onHotWidgetReplace = addWidgetsFromContext(widgets);
if (module.hot) {
    module.hot.accept(widgets.id, () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

const store = require.context("./Store", true);
store.keys().forEach(key => store(key));

// Load all custom pages so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
// const pages = require.context("./Pages", true, /\.tsx$/);
// const onHotPageReplace = addPagesFromContext(pages);
// if (module.hot) {
//     module.hot.accept(pages.id, () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
// }

// Load all handlers so they are included in the bundle.
const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key));

// in production, the widget extensions are included in the chunks created for widgets
if (!IS_PRODUCTION) {
    // load all widget extensions. They could be loaded individually instead
    const widgetExtensions = require.context("./WidgetExtensions", true);
    widgetExtensions.keys().forEach(key => widgetExtensions(key));
}

setPostStyleGuideTheme({
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: [0, 576, 768, 992, 1200],
        maxWidths: [576, 576, 768, 992, 1440],
    },
    typography: {
        fontFamilyImportUrl: "/staticContent/stylesheet.css",
        body: {
            fontFamily: "NotoSans-Regular",
            size: "15px",
        },
        p: {
            fontFamily: "NotoSans-Regular",
            size: "1rem",
        },
        headerPrimary: {
            fontFamily: "Poppins-Bold",
            size: "1.5rem",
        },
        headerSecondary: {
            fontFamily: "Poppins-Bold",
            size: "15px",
        },
        headerTertiary: {
            fontFamily: "NotoSans-Medium",
            size: "15px",
        },
        h1: {
            fontFamily: "Poppins-Bold",
            size: "2.25rem",
        },
        h2: {
            fontFamily: "Poppins-Bold",
            size: "1.5rem",
        },
        h3: {
            fontFamily: "Poppins-Bold",
            size: "1rem",
        },
        h4: {
            fontFamily: "Poppins-Bold",
            size: "15px",
        },
        h5: {
            fontFamily: "Poppins-Bold",
            size: "14px",
        },
        h6: {
            fontFamily: "Poppins-Bold",
            size: "13px",
        },
        legend: {
            fontFamily: "NotoSans-Medium",
            size: "13px",
        },
    },
    formField: {
        defaultProps: {
            cssOverrides: {
                formInputWrapper: css`
                    margin-top: 5px !important;
                `,
                inputSelect: css`
                    border: 1px solid ${getColor("text.main")};
                    &:focus {
                        outline-color: #09f;
                        outline-style: solid;
                        outline-width: 2px;
                        border-radius: 4px;
                    }
                    :-moz-placeholder {
                        color: #767676;
                        opacity: 1;
                    }

                    ::-moz-placeholder {
                        color: #767676;
                        opacity: 1;
                    }
                `,
            },
            labelProps: {
                color: "text.main",
                fontFamily: "NotoSans-Medium",
                size: "13px !important",
                transform: "capitalize",
                css: css`
                    > span {
                        color: ${getColor("primary")};
                    }
                `,
            },
        },
    },
    accordion: {
        sectionDefaultProps: {
            headerProps: {
                css: css`
                    button {
                        background: ${getColor("secondary.contrast")};
                        border: 1px solid ${getColor("common.border")};
                        > ${TypographyStyle} {
                            font-size: 1rem;
                            font-family: "NotoSans-Medium";
                            font-weight: normal;
                            color: #595b5d;
                        }

                        &:focus {
                            border: 1px solid #09f;
                            outline-color: #09f;
                            outline-style: solid;
                            outline-width: 2px;
                        }
                    }
                    button[aria-expanded="true"] {
                        outline-color: #09f;
                        outline-style: solid;
                        outline-width: 2px;
                        background: #595b5d;
                        > ${TypographyStyle} {
                            color: ${getColor("secondary.contrast")} !important;
                        }
                        > ${IconWrapper} {
                            color: ${getColor("secondary.contrast")};
                        }
                    }
                `,
            },

            panelProps: {
                css: css`
                    padding: 0.7rem 1.6rem;
                    border: none;
                    > ${GridWrapper} {
                        > ${GridOffset} {
                            > ${GridItemStyle} {
                                ${RadioStyle} {
                                    ${TypographyStyle} {
                                        font-size: 14px;
                                    }
                                }
                            }
                            > ${GridItemStyle}:last-child {
                                > ${TypographyStyle} {
                                    font-size: 1rem;
                                }
                            }
                        }
                    }
                `,
            },
            titleTypographyProps: {
                css: css`
                    color: ${getColor("primary")};
                `,
            },
            toggleIconProps: {
                css: css`
                    color: ${getColor("text.main")};
                `,
            },
        },
    },
    dataTable: {
        defaultProps: {
            cssOverrides: {
                head: css`
                    th {
                        border: 1px solid ${getColor("common.border")};
                        button {
                            height: 90%;
                            font-family: "NotoSans-Medium";
                        }
                    }
                `,
                body: css`
                    border: 1px solid ${getColor("common.border")};
                `,
                cell: css`
                    height: 60px;
                    border-right: 1px solid ${getColor("common.border")};
                `,
            },
        },
    },
    tag: {
        defaultProps: {
            css: css`
                background-color: ${getColor("secondary")};
                border-radius: 4px;
                color: ${getColor("secondary.contrast")};
                ${TypographyStyle} {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }
                > ${ButtonWrapper} {
                    background-color: transparent;
                }
            `,
        },
    },
    pagination: {
        defaultProps: {
            cssOverrides: {
                pagination: css`
                    padding: 5px;
                    border: 1px solid ${getColor("common.border")};
                `,
                currentButton: css`
                    cursor: default;
                    color: ${getProp("theme.colors.primary.contrast")};
                    background-color: ${getProp("theme.colors.primary.main")};
                    border-color: ${getProp("theme.colors.primary.main")};
                    min-height: 30px;
                `,
                linkList: css`
                    button {
                        width: 100% !important;
                    }
                `,
            },
        },
    },
    lists: {
        defaultProps: {
            css: css`
                margin: 0px;
                padding: 0px;
            `,
        },
    },
    link: {
        defaultProps: {
            color: "primary",
        },
    },
    checkbox: {
        defaultProps: {
            color: "#09f",
        },
    },
    button: {
        primary: {
            typographyProps: {
                fontFamily: "Poppins-Bold",
            },
        },
        secondary: {
            typographyProps: {
                fontFamily: "Poppins-Bold",
            },
        },
        tertiary: {
            typographyProps: {
                fontFamily: "Poppins-Bold",
            },
        },
    },
});
